import { QuickbooksQueryField, SsoQueryField } from '@common/neo.tax';

const filteredFromPageChangeQueryParams = [
  SsoQueryField.TOKEN,
  SsoQueryField.ERROR,
  'full_name',
  'firm_name',
  QuickbooksQueryField.CONNECTED_STATUS,
  'connected_payroll',
  'session_id',
  'apiKey',
  'oobCode',
  'mode',
  'lang',
  // email-verification flow (PLAT-242):
  // `verified=1` signals the success banner on /login after /verify-email;
  // `reason` drives copy on /verify-email-sent; `continueUrl` threads the
  // invitation token across the verify redirect. All three should be dropped
  // on any subsequent page change.
  'verified',
  'reason',
  'continueUrl',
  // Single-use auth param: consumed on the destination page (/accept-invitation
  // reads it via the derived continueUrl); carrying it forward into subsequent
  // navigations both clutters the URL and risks loops.
  'inviteToken',
  // Prefill-only hint for the signup/sign-in email field (set by the invite link and the
  // post-verification redirect). Dropped on incidental navigation so it doesn't linger in the
  // URL; the deliberate signup↔sign-in toggle preserves it separately via AuthFormHeader's
  // verbatim location search.
  'prefillEmail',
];

export const getFilteredQueryParams = (): URLSearchParams => {
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);
  filteredFromPageChangeQueryParams.forEach((param) => {
    queryParams.delete(param);
  });
  return queryParams;
};

/**
 * Merge a destination path with preserved current-URL params (those NOT in
 * `filteredFromPageChangeQueryParams`). Destination's own params take precedence,
 * so preserved params only fill in gaps. Handles destinations that already
 * contain a query string without producing the double-`?` URL that naive
 * string concatenation creates (`/accept-invitation?token=X?inviteToken=X`).
 */
export const mergeWithFilteredQueryParams = (to: string): string => {
  const preserved = getFilteredQueryParams();
  if (preserved.toString() === '') return to;
  // Split off any fragment first so merged params land before the `#`, not
  // inside it (`/path#section?userId=42` would make `userId` part of the fragment).
  const hashIdx = to.indexOf('#');
  const hash = hashIdx >= 0 ? to.slice(hashIdx) : '';
  const withoutHash = hashIdx >= 0 ? to.slice(0, hashIdx) : to;
  const [pathname, existingQuery = ''] = withoutHash.split('?');
  const merged = new URLSearchParams(existingQuery);
  preserved.forEach((value, key) => {
    if (!merged.has(key)) merged.set(key, value);
  });
  const mergedStr = merged.toString();
  return mergedStr === '' ? `${pathname}${hash}` : `${pathname}?${mergedStr}${hash}`;
};
